import { useState, useEffect, useRef } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from 'components/ui/button';
import {
  Bot, Plus, Edit, Trash2, X, Upload, Video, FileText,
  Loader2, Save, BookOpen, MessageSquare, Trash
} from 'lucide-react';

// ─── Badge inline ─────────────────────────────────────────────────────────────
const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default:     'bg-lime-400/20 text-lime-300 border border-lime-400/30',
    secondary:   'bg-white/10 text-white/70 border border-white/20',
    destructive: 'bg-red-400/20 text-red-300 border border-red-400/30',
    outline:     'border border-white/20 text-white/60',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant] || variants.default} ${className}`}>
      {children}
    </span>
  );
};

const difficultyVariant = (d) => {
  if (d === 'beginner') return 'default';
  if (d === 'intermediate') return 'secondary';
  return 'destructive';
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium text-white/50 uppercase tracking-wider">
      {label}
    </label>
    {children}
  </div>
);

// ─── Modal de Edición/Creación ────────────────────────────────────────────────
const EditModuleModal = ({ module, onClose, onSave, api, isSuperAdmin }) => {
  const isNew = !module?.id;

  const [form, setForm] = useState({
    title:       module?.title       || '',
    description: module?.description || '',
    content:     module?.content     || '',
    category:    module?.category    || '',
    difficulty:  module?.difficulty  || 'beginner',
    skills:      (module?.skills     || []).join(', '),
    image_url:   module?.image_url   || '',
    videos:      module?.videos      || [],
    roleplay_scenario: module?.roleplay_scenario || null,
  });

  const [saving, setSaving]           = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [activeTab, setActiveTab]     = useState('info');
  const [newVideo, setNewVideo]       = useState({ title: '', url: '', duration_seconds: 0, transcript: '' });
  const [addingVideo, setAddingVideo] = useState(false);
  
  // ✅ NUEVO: Estados para el editor de roleplay
  const [editingRoleplay, setEditingRoleplay] = useState(false);
  const [roleplayForm, setRoleplayForm] = useState({
    title: '',
    description: '',
    learning_objective: '',
    steps: []
  });
  const [addingStep, setAddingStep] = useState(false);
  const [newStep, setNewStep] = useState({ situation: '', expected_behavior: '' });

  const docInputRef = useRef(null);

  useEffect(() => {
    // Cargar roleplay existente si hay
    if (form.roleplay_scenario) {
      setRoleplayForm({
        title: form.roleplay_scenario.title || '',
        description: form.roleplay_scenario.description || '',
        learning_objective: form.roleplay_scenario.learning_objective || '',
        steps: form.roleplay_scenario.steps || []
      });
    }
  }, [form.roleplay_scenario]);

  const set = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const setRoleplay = (field) => (e) =>
    setRoleplayForm(prev => ({ ...prev, [field]: e.target.value }));

  // ── Subir documento ──────────────────────────────────────────────────────
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    toast.loading('Procesando documento con IA…', { id: 'doc-upload' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/modules/upload-and-generate', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(prev => ({
        ...prev,
        title:       res.data.title       || prev.title,
        description: res.data.description || prev.description,
        content:     res.data.content     || prev.content,
        category:    res.data.category    || prev.category,
        difficulty:  res.data.difficulty  || prev.difficulty,
        skills:      (res.data.skills     || []).join(', '),
        roleplay_scenario: res.data.roleplay_scenario || prev.roleplay_scenario,
      }));
      toast.success('¡Módulo generado con IA desde el documento!', { id: 'doc-upload' });
      setActiveTab('info');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: 'doc-upload' });
    } finally {
      setUploading(false);
    }
  };

  // ── Añadir video ──────────────────────────────────────────────────────────
  const addVideo = () => {
    if (!newVideo.title || !newVideo.url) {
      toast.error('El video necesita título y URL');
      return;
    }
    setForm(prev => ({
      ...prev,
      videos: [...prev.videos, { ...newVideo, id: crypto.randomUUID(), questions: [] }],
    }));
    setNewVideo({ title: '', url: '', duration_seconds: 0, transcript: '' });
    setAddingVideo(false);
  };

  const removeVideo = (idx) =>
    setForm(prev => ({ ...prev, videos: prev.videos.filter((_, i) => i !== idx) }));

  // ✅ NUEVO: Gestión de pasos del roleplay
  const addRoleplayStep = () => {
    if (!newStep.situation.trim()) {
      toast.error('La situación del paso no puede estar vacía');
      return;
    }
    setRoleplayForm(prev => ({
      ...prev,
      steps: [...prev.steps, { 
        id: `step${prev.steps.length + 1}`, 
        situation: newStep.situation,
        expected_behavior: newStep.expected_behavior || ''
      }]
    }));
    setNewStep({ situation: '', expected_behavior: '' });
    setAddingStep(false);
  };

  const removeRoleplayStep = (idx) => {
    setRoleplayForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx)
    }));
  };

  const moveRoleplayStep = (idx, direction) => {
    const newSteps = [...roleplayForm.steps];
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= newSteps.length) return;
    [newSteps[idx], newSteps[targetIdx]] = [newSteps[targetIdx], newSteps[idx]];
    // Reordenar IDs
    newSteps.forEach((step, i) => step.id = `step${i + 1}`);
    setRoleplayForm(prev => ({ ...prev, steps: newSteps }));
  };

  const saveRoleplay = () => {
    if (!roleplayForm.title.trim() || !roleplayForm.description.trim()) {
      toast.error('El roleplay necesita al menos título y descripción');
      return;
    }
    if (roleplayForm.steps.length === 0) {
      toast.error('Añade al menos un paso al roleplay');
      return;
    }
    setForm(prev => ({ ...prev, roleplay_scenario: roleplayForm }));
    setEditingRoleplay(false);
    toast.success('Roleplay guardado en el módulo');
  };

  const deleteRoleplay = () => {
    if (!confirm('¿Eliminar el roleplay del módulo?')) return;
    setForm(prev => ({ ...prev, roleplay_scenario: null }));
    setRoleplayForm({ title: '', description: '', learning_objective: '', steps: [] });
    setEditingRoleplay(false);
    toast.success('Roleplay eliminado');
  };

  // ── Guardar módulo ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title || !form.description || !form.category) {
      toast.error('Título, descripción y categoría son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
      };
      let res;
      if (isNew) {
        res = await api.post('/admin/modules', payload);
        toast.success('Módulo creado ✓');
      } else {
        res = await api.put(`/admin/modules/${module.id}`, payload);
        toast.success('Módulo actualizado ✓');
      }
      onSave(res.data);
      onClose();
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'info',     label: 'Información', icon: BookOpen },
    { id: 'content',  label: 'Contenido',   icon: FileText },
    { id: 'videos',   label: 'Videos',      icon: Video    },
    { id: 'roleplay', label: 'Roleplay',    icon: MessageSquare },
    // Pestaña de subir documentos solo para superadmin
    ...(isSuperAdmin ? [{ id: 'doc', label: 'Subir Doc', icon: Upload }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl bg-[#111] border border-white/10 shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">
            {isNew ? 'Nuevo Módulo' : `Editar: ${module.title}`}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 bg-black/20 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === id
                  ? 'border-lime-400 text-lime-400'
                  : 'border-transparent text-white/40 hover:text-white/70'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {id === 'roleplay' && form.roleplay_scenario && (
                <span className="w-2 h-2 bg-lime-400 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* TAB: Información */}
          {activeTab === 'info' && (
            <div className="space-y-4">
              <Field label="Título *">
                <input value={form.title} onChange={set('title')}
                  placeholder="Ej: Atención al Cliente Avanzado"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors" />
              </Field>
              <Field label="Descripción *">
                <textarea value={form.description} onChange={set('description')} rows={3}
                  placeholder="Describe brevemente el módulo…"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors resize-none" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Categoría *">
                  <input value={form.category} onChange={set('category')}
                    placeholder="Ej: Ventas, Técnico"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors" />
                </Field>
                <Field label="Dificultad">
                  <select value={form.difficulty} onChange={set('difficulty')}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 transition-colors">
                    <option value="beginner">Principiante</option>
                    <option value="intermediate">Intermedio</option>
                    <option value="advanced">Avanzado</option>
                  </select>
                </Field>
              </div>
              <Field label="Habilidades (separadas por coma)">
                <input value={form.skills} onChange={set('skills')}
                  placeholder="Ej: Comunicación, Negociación, Liderazgo"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors" />
              </Field>
              <Field label="URL de imagen de portada">
                <input value={form.image_url} onChange={set('image_url')}
                  placeholder="https://..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors" />
              </Field>
              {form.image_url && (
                <img src={form.image_url} alt="preview"
                  className="h-32 w-full rounded-xl object-cover border border-white/10" />
              )}
            </div>
          )}

          {/* TAB: Contenido */}
          {activeTab === 'content' && (
            <Field label="Contenido del módulo (Markdown)">
              <textarea value={form.content} onChange={set('content')} rows={20}
                placeholder={`# Título\n\n## Sección 1\nContenido...\n\n## Sección 2\nMás contenido...`}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors resize-y font-mono leading-relaxed" />
              <p className="text-xs text-white/30 mt-2">
                Usa <code className="text-lime-400/80">##</code> para crear secciones. Cada sección puede tener su propio test de IA.
              </p>
            </Field>
          )}

          {/* TAB: Videos */}
          {activeTab === 'videos' && (
            <div className="space-y-3">
              {form.videos.length === 0 && !addingVideo && (
                <div className="text-center py-10 text-white/25 border border-dashed border-white/10 rounded-xl">
                  <Video className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hay videos todavía</p>
                </div>
              )}

              {form.videos.map((video, idx) => (
                <div key={video.id || idx} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white text-sm truncate">{video.title}</p>
                    <p className="text-xs text-white/40 truncate mt-0.5">{video.url}</p>
                    <p className="text-xs text-white/30 mt-1">
                      {video.duration_seconds}s · {video.transcript ? 'Con transcripción' : 'Sin transcripción'}
                    </p>
                  </div>
                  <button onClick={() => removeVideo(idx)} className="text-red-400 hover:text-red-300 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {addingVideo && (
                <div className="p-4 bg-lime-400/5 rounded-xl border border-lime-400/20 space-y-3">
                  <p className="text-sm font-semibold text-lime-400">Nuevo video</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Título', field: 'title',            placeholder: 'Título del video',         type: 'text' },
                      { label: 'Duración (segundos)', field: 'duration_seconds', placeholder: '300', type: 'number' },
                    ].map(({ label, field, placeholder, type }) => (
                      <Field key={field} label={label}>
                        <input type={type} value={newVideo[field]}
                          onChange={e => setNewVideo(p => ({ ...p, [field]: type === 'number' ? +e.target.value : e.target.value }))}
                          placeholder={placeholder}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors" />
                      </Field>
                    ))}
                  </div>
                  <Field label="URL del video">
                    <input value={newVideo.url}
                      onChange={e => setNewVideo(p => ({ ...p, url: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors" />
                  </Field>
                  <Field label="Transcripción (opcional — mejora generación de tests)">
                    <textarea value={newVideo.transcript}
                      onChange={e => setNewVideo(p => ({ ...p, transcript: e.target.value }))}
                      rows={4} placeholder="Pega aquí la transcripción…"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-lime-400/50 placeholder-white/25 transition-colors resize-none" />
                  </Field>
                  <div className="flex gap-2">
                    <button onClick={addVideo}
                      className="px-4 py-2 bg-lime-400 text-black rounded-lg text-sm font-bold hover:bg-lime-300 transition-colors">
                      Añadir
                    </button>
                    <button onClick={() => setAddingVideo(false)}
                      className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {!addingVideo && (
                <button onClick={() => setAddingVideo(true)}
                  className="w-full py-3 border border-dashed border-white/15 rounded-xl text-white/35 hover:text-white/60 hover:border-white/30 transition-colors text-sm flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> Añadir video
                </button>
              )}
            </div>
          )}

          {/* ✅ TAB: ROLEPLAY */}
          {activeTab === 'roleplay' && (
            <div className="space-y-4">
              {!editingRoleplay && !form.roleplay_scenario && (
                <div className="text-center py-10 text-white/25 border border-dashed border-white/10 rounded-xl">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="mb-4">No hay juego de rol configurado</p>
                  <div className="flex justify-center gap-3">
                    <button onClick={() => setEditingRoleplay(true)}
                      className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700">
                      Crear Manualmente
                    </button>
                    {isSuperAdmin && (
                      <button 
                        onClick={async () => {
                          if (!module?.id) {
                            toast.error('Guarda el módulo primero antes de generar roleplay con IA');
                            return;
                          }
                          toast.loading('🤖 Generando roleplay con IA…', { id: 'roleplay-gen' });
                          try {
                            const res = await api.post(`/modules/${module.id}/generate-ai-content`);
                            setForm(prev => ({ 
                              ...prev, 
                              roleplay_scenario: res.data.roleplay_scenario 
                            }));
                            toast.success('✓ Roleplay generado con IA', { id: 'roleplay-gen' });
                          } catch (err) {
                            toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: 'roleplay-gen' });
                          }
                        }}
                        className="flex items-center gap-2 px-5 py-2 bg-lime-400 text-black rounded-lg text-sm font-semibold hover:bg-lime-300">
                        <Bot className="w-4 h-4" />
                        Generar con IA
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!editingRoleplay && form.roleplay_scenario && (
                <div className="space-y-3">
                  <div className="p-5 bg-purple-900/20 border border-purple-500/30 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-purple-400" />
                        <h3 className="font-bold text-white">{form.roleplay_scenario.title}</h3>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingRoleplay(true)}
                          className="p-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white/60 hover:text-white">
                          <Edit className="w-4 h-4" />
                        </button>
                        {isSuperAdmin && (
                          <button 
                            onClick={async () => {
                              if (!module?.id) {
                                toast.error('Guarda el módulo primero');
                                return;
                              }
                              if (!confirm('¿Regenerar el roleplay con IA? Esto reemplazará el roleplay actual.')) return;
                              toast.loading('🤖 Regenerando roleplay con IA…', { id: 'roleplay-regen' });
                              try {
                                const res = await api.post(`/modules/${module.id}/generate-ai-content`);
                                setForm(prev => ({ 
                                  ...prev, 
                                  roleplay_scenario: res.data.roleplay_scenario 
                                }));
                                toast.success('✓ Roleplay regenerado con IA', { id: 'roleplay-regen' });
                              } catch (err) {
                                toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: 'roleplay-regen' });
                              }
                            }}
                            className="p-1.5 bg-lime-400/20 rounded-lg hover:bg-lime-400/30 text-lime-400"
                            title="Regenerar con IA">
                            <Bot className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={deleteRoleplay}
                          className="p-1.5 bg-red-500/20 rounded-lg hover:bg-red-500/30 text-red-400">
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-white/60 text-sm mb-2">{form.roleplay_scenario.description}</p>
                    <p className="text-white/40 text-xs mb-3">
                      <strong className="text-purple-400">Objetivo:</strong> {form.roleplay_scenario.learning_objective}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-white/50">
                      <span className="px-2 py-1 bg-purple-500/20 rounded">
                        {form.roleplay_scenario.steps?.length || 0} pasos
                      </span>
                    </div>
                  </div>

                  {form.roleplay_scenario.steps?.map((step, idx) => (
                    <div key={step.id} className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <p className="text-xs text-white/40 mb-1">Paso {idx + 1}</p>
                      <p className="text-white text-sm">{step.situation}</p>
                      {step.expected_behavior && (
                        <p className="text-white/30 text-xs mt-2 italic">
                          Comportamiento esperado: {step.expected_behavior}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {editingRoleplay && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Editor de Roleplay</h3>
                    <div className="flex gap-2">
                      {isSuperAdmin && module?.id && (
                        <button 
                          onClick={async () => {
                            if (!confirm('¿Generar roleplay con IA? Esto reemplazará los cambios no guardados.')) return;
                            toast.loading('🤖 Generando roleplay con IA…', { id: 'roleplay-ai-gen' });
                            try {
                              const res = await api.post(`/modules/${module.id}/generate-ai-content`);
                              if (res.data.roleplay_scenario) {
                                setRoleplayForm({
                                  title: res.data.roleplay_scenario.title || '',
                                  description: res.data.roleplay_scenario.description || '',
                                  learning_objective: res.data.roleplay_scenario.learning_objective || '',
                                  steps: res.data.roleplay_scenario.steps || []
                                });
                                setForm(prev => ({ 
                                  ...prev, 
                                  roleplay_scenario: res.data.roleplay_scenario 
                                }));
                                toast.success('✓ Roleplay generado con IA', { id: 'roleplay-ai-gen' });
                              } else {
                                toast.error('No se pudo generar roleplay - contenido insuficiente', { id: 'roleplay-ai-gen' });
                              }
                            } catch (err) {
                              toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: 'roleplay-ai-gen' });
                            }
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-lime-400 text-black rounded-lg text-sm font-semibold hover:bg-lime-300">
                          <Bot className="w-4 h-4" />
                          Generar con IA
                        </button>
                      )}
                      <button onClick={() => setEditingRoleplay(false)}
                        className="text-white/40 hover:text-white text-sm px-3 py-1.5">
                        Cancelar
                      </button>
                    </div>
                  </div>

                  <Field label="Título del escenario *">
                    <input value={roleplayForm.title} onChange={setRoleplay('title')}
                      placeholder="Ej: Cliente Frustrado por Producto Defectuoso"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-purple-400/50 placeholder-white/25 transition-colors" />
                  </Field>

                  <Field label="Descripción del escenario *">
                    <textarea value={roleplayForm.description} onChange={setRoleplay('description')} rows={3}
                      placeholder="Describe la situación que enfrentará el usuario..."
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-purple-400/50 placeholder-white/25 transition-colors resize-none" />
                  </Field>

                  <Field label="Objetivo de aprendizaje">
                    <input value={roleplayForm.learning_objective} onChange={setRoleplay('learning_objective')}
                      placeholder="Ej: Aplicar técnicas de escucha activa y resolución de problemas"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-purple-400/50 placeholder-white/25 transition-colors" />
                  </Field>

                  {/* Lista de pasos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-white/50 uppercase tracking-wider">
                        Pasos del roleplay
                      </label>
                      <span className="text-xs text-white/30">{roleplayForm.steps.length} pasos</span>
                    </div>

                    {roleplayForm.steps.map((step, idx) => (
                      <div key={step.id} className="mb-3 p-4 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs font-semibold text-purple-400">Paso {idx + 1}</span>
                          <div className="flex gap-1">
                            <button onClick={() => moveRoleplayStep(idx, 'up')} disabled={idx === 0}
                              className="p-1 text-white/40 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
                              ↑
                            </button>
                            <button onClick={() => moveRoleplayStep(idx, 'down')} disabled={idx === roleplayForm.steps.length - 1}
                              className="p-1 text-white/40 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
                              ↓
                            </button>
                            <button onClick={() => removeRoleplayStep(idx)}
                              className="p-1 text-red-400 hover:text-red-300 ml-2">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <p className="text-white text-sm">{step.situation}</p>
                        {step.expected_behavior && (
                          <p className="text-white/30 text-xs mt-1 italic">
                            Esperado: {step.expected_behavior}
                          </p>
                        )}
                      </div>
                    ))}

                    {addingStep && (
                      <div className="p-4 bg-purple-900/20 rounded-xl border border-purple-500/30 space-y-3">
                        <p className="text-sm font-semibold text-purple-400">Nuevo paso</p>
                        <Field label="Situación / Diálogo *">
                          <textarea value={newStep.situation}
                            onChange={e => setNewStep(p => ({ ...p, situation: e.target.value }))}
                            rows={3}
                            placeholder='Ej: El cliente dice: "¡Esto es inaceptable! Es la segunda vez..."'
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-purple-400/50 placeholder-white/25 transition-colors resize-none" />
                        </Field>
                        <Field label="Comportamiento esperado (opcional)">
                          <input value={newStep.expected_behavior}
                            onChange={e => setNewStep(p => ({ ...p, expected_behavior: e.target.value }))}
                            placeholder="Ej: Escuchar sin interrumpir, mostrar empatía..."
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-purple-400/50 placeholder-white/25 transition-colors" />
                        </Field>
                        <div className="flex gap-2">
                          <button onClick={addRoleplayStep}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors">
                            Añadir paso
                          </button>
                          <button onClick={() => { setAddingStep(false); setNewStep({ situation: '', expected_behavior: '' }); }}
                            className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {!addingStep && (
                      <button onClick={() => setAddingStep(true)}
                        className="w-full py-3 border border-dashed border-white/15 rounded-xl text-white/35 hover:text-white/60 hover:border-white/30 transition-colors text-sm flex items-center justify-center gap-2">
                        <Plus className="w-4 h-4" /> Añadir paso
                      </button>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-white/10">
                    <button onClick={saveRoleplay}
                      className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700">
                      Guardar roleplay
                    </button>
                    <button onClick={() => setEditingRoleplay(false)}
                      className="px-4 py-2.5 bg-white/10 text-white rounded-xl text-sm hover:bg-white/20">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB: Subir Documento */}
          {activeTab === 'doc' && (
            <div className="space-y-4">
                <button type="button" onClick={() => docInputRef.current?.click()} disabled={uploading}
  className={`w-full relative flex flex-col items-center justify-center gap-4 p-14 rounded-2xl border-2 border-dashed transition-all cursor-pointer
                  ${uploading ? 'border-lime-400/40 bg-lime-400/5 cursor-not-allowed' : 'border-white/15 hover:border-lime-400/40 hover:bg-lime-400/5'}`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-12 h-12 text-lime-400 animate-spin" />
                    <p className="text-white font-semibold">Procesando con IA…</p>
                    <p className="text-white/40 text-sm">Esto puede tardar unos segundos</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-white/25" />
                    <div className="text-center">
                      <p className="text-white font-semibold mb-1">Arrastra o haz clic para subir</p>
                      <p className="text-white/40 text-sm">PDF, DOCX o TXT</p>
                    </div>
                    <span className="px-5 py-2 bg-lime-400 text-black rounded-xl text-sm font-bold">
                      Seleccionar archivo
                    </span>
                  </>
                )}
                </button>
<input ref={docInputRef} type="file" accept=".pdf,.docx,.doc,.txt"
  className="hidden" onChange={handleDocUpload} disabled={uploading} />

              <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10 text-sm text-white/40 space-y-1.5">
                <p className="text-white/60 font-semibold mb-2">¿Cómo funciona?</p>
                <p>① Sube un PDF, DOCX o TXT con el material del módulo</p>
                <p>② La IA extrae el texto y genera: título, descripción, categoría, preguntas y roleplay</p>
                <p>③ Revisa y edita todo antes de guardar</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-black/20">
          <button onClick={onClose} className="px-4 py-2 text-sm text-white/40 hover:text-white transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-lime-400 text-black rounded-xl text-sm font-bold hover:bg-lime-300 disabled:opacity-50 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? 'Crear módulo' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Panel principal ──────────────────────────────────────────────────────────
const ModuleManagementPanel = () => {
  const { api, user }                         = useAuth();
  const { orgId }                             = useParams();
  const navigate                              = useNavigate();
  const [modules, setModules]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [editingModule, setEditingModule]     = useState(null);
  const [generatingTests, setGeneratingTests] = useState(null);

  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => { fetchModules(); }, [orgId]);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/modules?organization_id=${orgId || ''}`);
      setModules(res.data);
    } catch (err) {
      toast.error('Error al cargar módulos: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (saved) => {
    setModules(prev => {
      const idx = prev.findIndex(m => m.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [saved, ...prev];
    });
  };

  const handleDelete = async (moduleId, title) => {
    if (!confirm(`¿Eliminar "${title}"?`)) return;
    try {
      await api.delete(`/admin/modules/${moduleId}`);
      setModules(prev => prev.filter(m => m.id !== moduleId));
      toast.success('Módulo eliminado');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleGenerateAI = async (moduleId, title) => {
    if (!confirm(`¿Generar contenido IA para "${title}"?\n\nEsto incluye:\n• Preguntas para cada video\n• Roleplay basado en todo el módulo`)) return;
    toast.loading('🤖 Generando preguntas y roleplay con IA…', { id: 'ai-gen' });
    try {
      const res = await api.post(`/modules/${moduleId}/generate-ai-content`);
      toast.success(`✓ Contenido generado con IA (preguntas + roleplay)`, { id: 'ai-gen' });
      fetchModules();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: 'ai-gen' });
    }
  };

  const handleGenerateTests = async (module) => {
    let content = module.content?.length > 100
      ? module.content
      : module.videos?.length > 0
        ? module.videos.map((v, i) => `### Video ${i+1}: ${v.title}\n${v.transcript||''}`).join('\n\n')
        : module.description || '';

    if (content.length < 100) {
      toast.error('Contenido insuficiente para generar tests');
      return;
    }
    setGeneratingTests(module.id);
    toast.loading('🤖 Generando tests…', { id: 'test-gen' });
    try {
      const res = await api.post(`/admin/modules/${module.id}/generate-tests`, {
        sections: [{ id: 'section_1', title: module.title, content }],
      });
      toast.success(`${res.data.tests_generated} test(s) generado(s) ✓`, { id: 'test-gen' });
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message), { id: 'test-gen' });
    } finally {
      setGeneratingTests(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-16">
      <Loader2 className="w-8 h-8 animate-spin text-lime-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Gestión de Módulos</h1>
        <button onClick={() => setEditingModule({ _isNew: true })}
          className="flex items-center gap-2 px-5 py-2.5 bg-lime-400 text-black rounded-xl font-bold text-sm hover:bg-lime-300 transition-colors">
          <Plus className="w-4 h-4" /> Crear Módulo
        </button>
      </div>

      {/* Empty state */}
      {modules.length === 0 && (
        <div className="text-center py-20 text-white/20 border border-white/5 rounded-2xl">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="text-lg">No hay módulos todavía</p>
        </div>
      )}

      {/* Lista */}
      <div className="grid gap-4">
        {modules.map((module) => (
          <div key={module.id}
            className="flex items-start gap-4 p-5 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-white/20 transition-all">
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white text-lg leading-tight mb-1">{module.title}</h3>
              <p className="text-white/40 text-sm line-clamp-2 mb-3">{module.description}</p>
              <div className="flex flex-wrap gap-2">
                {module.category  && <Badge>{module.category}</Badge>}
                {module.difficulty && <Badge variant={difficultyVariant(module.difficulty)}>{module.difficulty}</Badge>}
                {(module.skills || []).map(s => <Badge key={s} variant="outline">{s}</Badge>)}
                {module.roleplay_scenario && (
                  <Badge variant="secondary">
                    <MessageSquare className="w-3 h-3 mr-1" /> Roleplay
                  </Badge>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
              {/* Generar IA - SOLO SUPERADMIN */}
              {isSuperAdmin && (
                <button onClick={() => handleGenerateAI(module.id, module.title)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/25 text-sm transition-all">
                  <Bot className="w-4 h-4" />
                  <span className="hidden md:inline">Generar IA</span>
                </button>
              )}

              {/* Generar Tests - SOLO SUPERADMIN */}
              {isSuperAdmin && (
                <button
                  onClick={() => handleGenerateTests(module)}
                  disabled={generatingTests === module.id}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/25 text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                  {generatingTests === module.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <FileText className="w-4 h-4" />}
                  <span className="hidden md:inline">
                    {generatingTests === module.id ? 'Generando…' : 'Tests IA'}
                  </span>
                </button>
              )}

              {/* Editar */}
              <button
                onClick={() => setEditingModule(module)}
                title="Editar módulo"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-lime-400 hover:border-lime-400/30 text-sm transition-all">
                <Edit className="w-4 h-4" />
                <span className="hidden md:inline">Editar</span>
              </button>

              {/* Eliminar */}
              <button
                onClick={() => handleDelete(module.id, module.title)}
                className="p-2 rounded-lg bg-red-500/5 border border-red-500/15 text-red-400 hover:bg-red-500/10 hover:border-red-500/35 transition-all">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {editingModule && (
        <EditModuleModal
          module={editingModule?._isNew ? null : editingModule}
          api={api}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditingModule(null)}
          onSave={handleSaved}
        />
      )}
    </div>
  );
};

export default ModuleManagementPanel;